/**
 * Membership status and navigation to subscription flows.
 */
import { Navigate, useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import PageShell from "../components/ui/PageShell";
import GlassPanel from "../components/ui/GlassPanel";
import Button from "../components/ui/Button";
import { cancelPayPalSubscription } from "../scripts/api";
import { useLoadingOverlay } from "../context/LoadingOverlayContext";

function addDays(dateValue, days) {
  const date = new Date(dateValue);
  date.setDate(date.getDate() + days);
  return date;
}

function parseDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString();
}

/**
 * PayPal trial was ended in PayPal (subscription canceled) — not the same as trial expired naturally.
 * @param {Record<string, unknown>|null|undefined} user
 * @returns {boolean}
 */
export function isPayPalTrialUserCanceled(user) {
  if (!user) return false;
  const ps = String(user.paypal_status ?? "").trim().toUpperCase();
  if (ps !== "CANCELLED") return false;
  const lps = String(user.last_payment_status ?? "").trim().toLowerCase();
  if (lps !== "trial") return false;
  const payTier = String(user.pay_tier ?? "").trim().toLowerCase();
  const r = String(user.role ?? "").trim().toLowerCase();
  if (payTier === "active" && r === "member") return false;
  return true;
}

/**
 * End of the free-trial window (site trial or PayPal trial).
 * @param {Record<string, unknown>} user
 * @returns {Date|null}
 */
export function getTrialPeriodEnd(user) {
  if (isPayPalTrialUserCanceled(user)) {
    return null;
  }
  const payTier = String(user?.pay_tier || "").trim().toLowerCase();
  const subExp = parseDate(user?.subscription_expires_at);
  const lastPaid = parseDate(user?.last_paid);
  const usedTrialAt = parseDate(user?.used_trial_at);
  const lps = String(user?.last_payment_status || "").trim().toLowerCase();

  if (payTier === "trial" && subExp && lps === "trial") {
    return subExp;
  }
  if (payTier === "trial" && lastPaid) {
    return addDays(lastPaid, 7);
  }
  if (usedTrialAt) {
    return addDays(usedTrialAt, 7);
  }
  return null;
}

/**
 * End of current paid period (one-time subscription), not used when auto-renew.
 * @param {Record<string, unknown>} user
 * @returns {Date|null}
 */
export function getSubscriptionPeriodEnd(user) {
  const payTier = String(user?.pay_tier || "").trim().toLowerCase();
  const role = String(user?.role || "").trim().toLowerCase();
  if (payTier !== "active" || role !== "member") {
    return null;
  }
  if (Boolean(user?.auto_renew)) {
    return null;
  }
  const fromDb = parseDate(user?.subscription_expires_at);
  if (fromDb) {
    return fromDb;
  }
  const lastPaid = parseDate(user?.last_paid);
  if (!lastPaid) {
    return null;
  }
  const annualSub = Boolean(user?.annual_sub);
  return addDays(lastPaid, annualSub ? 365 : 30);
}

/**
 * Next renewal / billing instant when auto-renew is on.
 * @param {Record<string, unknown>} user
 * @returns {Date|null}
 */
export function getSubscriptionRenewsAt(user) {
  if (!Boolean(user?.auto_renew)) {
    return null;
  }
  return parseDate(user?.subscription_expires_at);
}

/**
 * True when the user has a PayPal subscription that is the yearly trial plan (or marked trial in DB).
 * @param {Record<string, unknown>|null|undefined} user
 * @returns {boolean}
 */
export function isUserOnPayPalTrial(user) {
  if (!user) return false;
  if (String(user.paypal_status ?? "").trim().toUpperCase() === "CANCELLED") {
    return false;
  }
  if (!user.paypal_subscription_id) {
    return false;
  }
  const trialPlanEnv = String(process.env.REACT_APP_PAYPAL_PLAN_YEAR_TRIAL_ONCE ?? "").trim();
  const userPlanId = String(user.paypal_plan_id ?? "").trim();
  return (
    String(user.last_payment_status ?? "").toLowerCase() === "trial" ||
    String(user.pay_tier ?? "").toLowerCase() === "trial" ||
    (trialPlanEnv !== "" && userPlanId === trialPlanEnv)
  );
}

/**
 * @param {Record<string, unknown>|null|undefined} user
 * @param {() => string} paidEntryPath
 * @returns {{ id: string, label: string, variant?: string, to?: string, action?: 'cancelPayPalTrial' | 'cancelPayPalSubscription' }}
 */
function buildEndTrialButton(user, paidEntryPath) {
  if (isUserOnPayPalTrial(user)) {
    return { id: "end-trial", label: "End Trial Period", variant: "outline", action: "cancelPayPalTrial" };
  }
  return { id: "end-trial", label: "End Trial Period", variant: "outline", to: paidEntryPath() };
}

/**
 * @typedef {{ id: string, label: string, variant?: 'outline'|'solid', to?: string, action?: 'cancelPayPalTrial' | 'cancelPayPalSubscription' }} MembershipButton
 * @typedef {{
 *   statusText: string,
 *   trialExpires: Date|null,
 *   subscriptionExpires: Date|null,
 *   subscriptionRenews: Date|null,
 *   buttons: MembershipButton[],
 * }} MembershipPresentation
 */

/**
 * Pure presentation rules from session user (see product matrix in repo docs).
 * @param {Record<string, unknown>|null|undefined} user
 * @returns {MembershipPresentation}
 */
export function getMembershipPresentation(user) {
  const role = String(user?.role || "").trim().toLowerCase();
  const payTier = String(user?.pay_tier || "").trim().toLowerCase();
  const autoRenew = Boolean(user?.auto_renew);
  const usedTrial = Boolean(user?.used_trial);
  const now = new Date();

  if (role === "owner" || role === "admin") {
    return {
      statusText: "Subscription Active",
      trialExpires: null,
      subscriptionExpires: null,
      subscriptionRenews: null,
      buttons: [],
    };
  }

  const isMember = role === "member";
  const subscriptionActivated = payTier === "active" && isMember;
  const paidPath = () => "/subscription?entry=paid";
  const trialPath = () => "/subscription?entry=trial";

  if (isPayPalTrialUserCanceled(user) && !subscriptionActivated) {
    return {
      statusText: "Trial Period Canceled",
      trialExpires: null,
      subscriptionExpires: null,
      subscriptionRenews: null,
      buttons: [{ id: "subscribe", label: "Subscribe", variant: "solid", to: paidPath() }],
    };
  }

  const trialEnd = getTrialPeriodEnd(user);
  const trialActive = Boolean(usedTrial && trialEnd && now < trialEnd);
  const trialExpired = Boolean(usedTrial && trialEnd && now >= trialEnd);
  const subEnd = getSubscriptionPeriodEnd(user);
  const subscriptionExpiredOneTime = Boolean(
    subscriptionActivated && !autoRenew && subEnd && now >= subEnd
  );
  const subscriptionActive = Boolean(
    subscriptionActivated &&
      (autoRenew || (subEnd ? now < subEnd : Boolean(parseDate(user?.last_paid))))
  );
  const renewsAt = getSubscriptionRenewsAt(user);

  const subAccessLapsed = Boolean(
    payTier === "inactive" &&
      role === "guest" &&
      (Boolean(user?.paypal_subscription_id) ||
        String(user?.last_payment_status || "")
          .trim()
          .toLowerCase() === "completed")
  );

  /** @type {MembershipPresentation} */
  const base = {
    statusText: "Subscription Inactive",
    trialExpires: null,
    subscriptionExpires: null,
    subscriptionRenews: null,
    buttons: [],
  };

  // Trial window still open + paid subscription active — UI treats as subscription only (trial PayPal plan canceled when subscribing)
  if (trialActive && subscriptionActivated && subscriptionActive && autoRenew) {
    return {
      statusText: "Subscription Active",
      trialExpires: null,
      subscriptionExpires: null,
      subscriptionRenews: renewsAt,
      buttons: [{ id: "cancel-sub", label: "Cancel Subscription", variant: "outline", action: "cancelPayPalSubscription" }],
    };
  }

  if (trialActive && subscriptionActivated && subscriptionActive && !autoRenew) {
    return {
      statusText: "Subscription Active",
      trialExpires: null,
      subscriptionExpires: subEnd,
      subscriptionRenews: null,
      buttons: [],
    };
  }

  // 6 — Trial not activated, sub active, not expired, auto-renew
  if (!usedTrial && subscriptionActivated && subscriptionActive && autoRenew) {
    return {
      statusText: "Subscription Active",
      trialExpires: null,
      subscriptionExpires: null,
      subscriptionRenews: renewsAt,
      buttons: [{ id: "cancel-sub", label: "Cancel Subscription", variant: "outline", action: "cancelPayPalSubscription" }],
    };
  }

  // 4 — Trial not activated, sub active, not expired, no auto-renew
  if (!usedTrial && subscriptionActivated && subscriptionActive && !autoRenew) {
    return {
      statusText: "Subscription Active",
      trialExpires: null,
      subscriptionExpires: subEnd,
      subscriptionRenews: null,
      buttons: [],
    };
  }

  // 12 — Trial expired, sub active, not expired, auto-renew
  if (trialExpired && subscriptionActivated && subscriptionActive && autoRenew) {
    return {
      statusText: "Subscription Active",
      trialExpires: null,
      subscriptionExpires: null,
      subscriptionRenews: renewsAt,
      buttons: [{ id: "cancel-sub", label: "Cancel Subscription", variant: "outline", action: "cancelPayPalSubscription" }],
    };
  }

  // 8 — Trial expired, sub active, not expired, no auto-renew
  if (trialExpired && subscriptionActivated && subscriptionActive && !autoRenew) {
    return {
      statusText: "Subscription Active",
      trialExpires: null,
      subscriptionExpires: subEnd,
      subscriptionRenews: null,
      buttons: [],
    };
  }

  // 5 — Trial not activated, subscription expired (active row past end, or lapsed inactive guest)
  if (!usedTrial && !autoRenew && (subscriptionExpiredOneTime || subAccessLapsed)) {
    return {
      statusText: "Subscription Expired",
      trialExpires: null,
      subscriptionExpires: null,
      subscriptionRenews: null,
      buttons: [
        { id: "activate-trial", label: "Activate Trial Period", variant: "outline", to: trialPath() },
        { id: "subscribe", label: "Subscribe", variant: "solid", to: paidPath() },
      ],
    };
  }

  // 10 — Trial expired, subscription expired (incl. lapsed to inactive guest after PayPal)
  if (
    usedTrial &&
    trialExpired &&
    !autoRenew &&
    (subscriptionExpiredOneTime || subAccessLapsed)
  ) {
    return {
      statusText: "Subscription Expired",
      trialExpires: null,
      subscriptionExpires: null,
      subscriptionRenews: null,
      buttons: [{ id: "subscribe", label: "Subscribe", variant: "solid", to: paidPath() }],
    };
  }

  // 2 — Trial active, subscription not active, trial not expired, no auto-renew
  if (trialActive && !subscriptionActivated && !autoRenew) {
    return {
      statusText: "Trial Period Active",
      trialExpires: trialEnd,
      subscriptionExpires: null,
      subscriptionRenews: null,
      buttons: [buildEndTrialButton(user, paidPath), { id: "subscribe", label: "Subscribe", variant: "solid", to: paidPath() }],
    };
  }

  // 3 — Trial expired, subscription not activated / not in lapsed-paid state
  if (
    usedTrial &&
    trialExpired &&
    !subscriptionActivated &&
    !subscriptionExpiredOneTime &&
    !subAccessLapsed &&
    !autoRenew
  ) {
    return {
      statusText: "Trial Period Expired",
      trialExpires: null,
      subscriptionExpires: null,
      subscriptionRenews: null,
      buttons: [{ id: "subscribe", label: "Subscribe", variant: "solid", to: paidPath() }],
    };
  }

  // 1 — Fully inactive / eligible (no trial used, no active subscription, not lapsed paid)
  if (!usedTrial && !subscriptionActivated && !autoRenew && !subAccessLapsed) {
    return {
      ...base,
      statusText: "Subscription Inactive",
      buttons: [
        { id: "start-trial", label: "Start Trial Period", variant: "outline", to: trialPath() },
        { id: "subscribe", label: "Subscribe", variant: "solid", to: paidPath() },
      ],
    };
  }

  // Fallback: guest / trial / active edge states not covered above
  if (payTier === "trial" && usedTrial && trialEnd) {
    const active = now < trialEnd;
    return {
      statusText: active ? "Trial Period Active" : "Trial Period Expired",
      trialExpires: active ? trialEnd : null,
      subscriptionExpires: null,
      subscriptionRenews: null,
      buttons: active
        ? [buildEndTrialButton(user, paidPath), { id: "subscribe", label: "Subscribe", variant: "solid", to: paidPath() }]
        : [{ id: "subscribe", label: "Subscribe", variant: "solid", to: paidPath() }],
    };
  }

  return {
    ...base,
    buttons: [
      { id: "start-trial", label: "Start Trial Period", variant: "outline", to: trialPath() },
      { id: "subscribe", label: "Subscribe", variant: "solid", to: paidPath() },
    ],
  };
}

export default function Membership({ session, loading, refreshSession }) {
  const navigate = useNavigate();
  const { showLoading, hideLoading } = useLoadingOverlay();
  const [membershipActionBusy, setMembershipActionBusy] = useState(false);
  const [actionMessage, setActionMessage] = useState("");
  const user = session?.user || null;

  const membership = useMemo(() => getMembershipPresentation(user), [user]);

  if (loading) return null;
  if (!session?.authenticated || !user) return <Navigate to="/" replace />;

  async function handleMembershipButtonClick(btn) {
    if (btn.action === "cancelPayPalTrial") {
      setActionMessage("");
      setMembershipActionBusy(true);
      showLoading("Ending trial…");
      try {
        await cancelPayPalSubscription();
        await refreshSession?.();
      } catch (err) {
        setActionMessage(err?.message || "Could not end trial.");
      } finally {
        hideLoading();
        setMembershipActionBusy(false);
      }
      return;
    }
    if (btn.action === "cancelPayPalSubscription") {
      setActionMessage("");
      setMembershipActionBusy(true);
      showLoading("Canceling subscription…");
      try {
        await cancelPayPalSubscription();
        await refreshSession?.();
      } catch (err) {
        setActionMessage(err?.message || "Could not cancel subscription.");
      } finally {
        hideLoading();
        setMembershipActionBusy(false);
      }
      return;
    }
    if (btn.to) {
      navigate(btn.to);
    }
  }

  return (
    <PageShell>
      <GlassPanel>
        <div className="space-y-6">
          <h1 className="text-lg font-medium leading-relaxed text-white/90">Membership</h1>

          <hr className="border-white/10" />

          <p className="text-base font-bold leading-relaxed text-white">
            Status: {membership.statusText}
          </p>

          {membership.trialExpires ? (
            <div className="space-y-2">
              <h2 className="text-base font-bold text-white">Trial Period Expires:</h2>
              <p className="leading-relaxed text-white/90">{formatDate(membership.trialExpires)}</p>
            </div>
          ) : null}

          {membership.subscriptionExpires ? (
            <div className="space-y-2">
              <h2 className="text-base font-bold text-white">Subscription Expires:</h2>
              <p className="leading-relaxed text-white/90">{formatDate(membership.subscriptionExpires)}</p>
            </div>
          ) : null}

          {membership.subscriptionRenews ? (
            <div className="space-y-2">
              <h2 className="text-base font-bold text-white">Subscription Renews:</h2>
              <p className="leading-relaxed text-white/90">{formatDate(membership.subscriptionRenews)}</p>
            </div>
          ) : null}

          {actionMessage ? (
            <p className="text-sm text-white/80" role="status">
              {actionMessage}
            </p>
          ) : null}

          {membership.buttons.length > 0 ? (
            <div className="flex flex-col gap-3 sm:flex-row">
              {membership.buttons.map((btn) => (
                <Button
                  key={btn.id}
                  type="button"
                  variant={btn.variant === "solid" ? "solid" : "outline"}
                  disabled={membershipActionBusy}
                  onClick={() => handleMembershipButtonClick(btn)}
                >
                  {btn.label}
                </Button>
              ))}
            </div>
          ) : null}
        </div>
      </GlassPanel>
    </PageShell>
  );
}
